import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X, Maximize2, Minimize2 } from "lucide-react"

import { cn } from "@/lib/utils"

// iter248 — "expand modal" preference. STICKY by user request: once a modal
// is expanded, every modal afterwards opens expanded until one is collapsed.
// Stored on the device (localStorage) so it also survives a reload.
const EXPAND_KEY = "dotbox_modal_expanded"

const readExpandPref = () => {
  try {
    return localStorage.getItem(EXPAND_KEY) === "1"
  } catch {
    return false
  }
}

const writeExpandPref = (value) => {
  try {
    localStorage.setItem(EXPAND_KEY, value ? "1" : "0")
  } catch {
    /* private mode / quota — the toggle still works for this modal */
  }
}

// Near-fullscreen, not edge-to-edge, so it still reads as a window.
// `sm:` variants are emitted too: 22 call sites use `sm:max-w-*`, and a
// responsive utility would otherwise out-rank the unprefixed override.
const EXPANDED_CLASSES =
  "w-[96vw] max-w-[96vw] sm:max-w-[96vw] h-[96vh] max-h-[96vh]"

// Only modals with something to gain get the toggle (user's choice). Signal =
// the call site asked to be wide, tall, or scrollable. A plain confirm dialog
// with no size classes gets nothing.
const WIDE = /(?:^|\s)(?:sm:|md:|lg:)?max-w-(?:2xl|3xl|4xl|5xl|6xl|7xl|full|screen-\w+)(?:\s|$)/
const hasSubstantialContent = (className) => {
  const c = typeof className === "string" ? className : ""
  if (!c) return false
  return (
    WIDE.test(c) ||
    /(?:^|\s)w-\[/.test(c) ||
    /max-h-\[/.test(c) ||
    /(?:^|\s)h-\[/.test(c) ||
    c.includes("overflow")
  )
}

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/**
 * `expandable` — override the auto-detection:
 *   undefined (default) → decided by hasSubstantialContent(className)
 *   true / false        → force the toggle on or off for this modal
 */
const DialogContent = React.forwardRef(({ className, children, expandable, ...props }, ref) => {
  const canExpand = expandable === undefined ? hasSubstantialContent(className) : expandable
  const [expanded, setExpanded] = React.useState(() => canExpand && readExpandPref())

  const toggleExpanded = () => {
    setExpanded((prev) => {
      writeExpandPref(!prev)
      return !prev
    })
  }

  const isExpanded = canExpand && expanded

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        data-dialog-expanded={isExpanded ? "true" : undefined}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          className,
          // Last so tailwind-merge drops the call site's own size utilities.
          isExpanded && EXPANDED_CLASSES,
          isExpanded && "overflow-y-auto"
        )}
        {...props}>
        {children}
        {canExpand && (
          <button
            type="button"
            onClick={toggleExpanded}
            className="absolute right-12 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            data-testid="dialog-expand-toggle"
            aria-pressed={isExpanded}
            aria-label={isExpanded ? "Collapse modal" : "Expand modal"}
            title={isExpanded ? "Collapse modal" : "Expand modal to fill the window"}
          >
            {isExpanded
              ? <Minimize2 className="h-4 w-4" />
              : <Maximize2 className="h-4 w-4" />}
            <span className="sr-only">
              {isExpanded ? "Collapse modal" : "Expand modal"}
            </span>
          </button>
        )}
        <DialogPrimitive.Close
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  // iter248 — `pr-10` reserves the top-right gutter occupied by the close X
  // and the new expand toggle. Without it, right-aligned header content
  // (status chips, badges) renders UNDERNEATH those controls. The dialog pads
  // itself by 24px and the control cluster reaches 64px inward, so the header
  // needs 40px of its own clearance.
  <div
    className={cn("flex flex-col space-y-1.5 pr-10 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
